// src/components/PalletsTable.js
import React, { useState } from 'react';
import DataTable from 'react-data-table-component';
import {
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import styled from 'styled-components';
import { format } from 'date-fns';
import { getPalletContent } from 'services/traceabilityProductionReport/service';

const TableWrapper = styled(Card)`
  margin-top: 12px;
  border-radius: 10px !important;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
`;

const BagsTableWrapper = styled.div`
  margin-top: 8px;
`;

const DialogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return format(d, 'dd/MM/yyyy HH:mm');
}

function formatNumber2(value) {
  if (value === null || value === undefined) return '-';
  return Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMinutes(value) {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * props:
 *   data: array de paletes [{ palletId, closeTime, packageCount, totalWeight, avgWeight, buildTimeMinutes }]
 *   productionLot: string (para mandar na query do backend ao buscar bags)
 */
const PalletsTable = ({ data, productionLot }) => {
  const [bagsModalOpen, setBagsModalOpen] = useState(false);
  const [bagsLoading, setBagsLoading] = useState(false);
  const [bags, setBags] = useState([]);
  const [selectedPalletId, setSelectedPalletId] = useState(null);

  const handleCloseModal = () => {
    setBagsModalOpen(false);
    setSelectedPalletId(null);
    setBags([]);
  };

  const handleOpenBags = async (palletId) => {
    setSelectedPalletId(palletId);
    setBagsModalOpen(true);
    setBags([]);
    setBagsLoading(true);

    try {
      getPalletContent(palletId).then(data => {
        setBags(data.data);
      });

    } catch (error) {
      console.error('Erro ao carregar bags do palete', error);
    } finally {
      setBagsLoading(false);
    }
  };

  const columns = [
    {
      name: 'Palete',
      selector: 'palletId',
      sortable: true,
      cell: (row) => row.palletId || '-',
    },
    {
      name: 'Fechamento',
      selector: 'closeTime',
      sortable: true,
      cell: (row) => formatDateTime(row.closeTime),
    },
    {
      name: 'Emb.',
      selector: 'packageCount',
      sortable: true,
      right: true,
      cell: (row) =>
        row.packageCount != null ? row.packageCount.toLocaleString('pt-BR') : '-',
    },
    {
      name: 'Peso total (kg)',
      selector: 'totalWeight',
      sortable: true,
      right: true,
      cell: (row) => formatNumber2(row.totalWeight),
    },
    {
      name: 'Peso médio (kg)',
      selector: 'avgWeight',
      sortable: true,
      right: true,
      cell: (row) => formatNumber2(row.avgWeight),
    },
    {
      name: 'Tempo montagem (min)',
      selector: 'buildTimeMinutes',
      sortable: true,
      right: true,
      cell: (row) => formatMinutes(row.buildTimeMinutes),
    },
    {
      name: 'Ações',
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      cell: (row) => (
        <button
          type="button"
          onClick={() => handleOpenBags(row.palletId)}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#1976d2',
            cursor: 'pointer',
            padding: 0,
            fontSize: 13,
          }}
        >
          Ver embalagens
        </button>
      ),
    },
  ];

  const bagsColumns = [
    {
      name: 'Embalagem',
      selector: 'bagIdentifier',
      sortable: true,
    },
    {
      name: 'Peso (kg)',
      selector: 'weight',
      sortable: true,
      right: true,
      cell: (row) => formatNumber2(row.weight),
      maxWidth: "10%",
    },
    {
      name: 'Linha',
      selector: 'productionLine',
      sortable: true,
      right: true,
      maxWidth: "5%",
    },
    {
      name: 'Sequência',
      selector: 'bagSequence',
      sortable: true,
      right: true,
    },
    {
      name: 'Lido em',
      selector: (row) => row.readedAt || row.createdAt,
      sortable: true,
      cell: (row) => formatDateTime(row.readedAt || row.createdAt),
    },
  ];

  return (
    <>
      <TableWrapper elevation={2}>
        <DataTable
          title="Paletes"
          columns={columns}
          data={data}
          dense
          pagination
          striped
          highlightOnHover
          noDataComponent="Nenhum palete encontrado."
        />
      </TableWrapper>

      <Dialog
        open={bagsModalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle disableTypography>
          <DialogHeader>
            <div>
              <Typography variant="h6">
                Palete {selectedPalletId || ''}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Embalagens do palete
              </Typography>
            </div>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </DialogHeader>
        </DialogTitle>

        <DialogContent dividers>
          {bagsLoading ? (
            <Typography>Carregando embalagens...</Typography>
          ) : (
            <BagsTableWrapper>
              <DataTable
                columns={bagsColumns}
                data={bags}
                dense
                pagination
                striped
                highlightOnHover
                noDataComponent="Nenhuma embalagem encontrada para este palete."
              />
            </BagsTableWrapper>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PalletsTable;
